/**
 * Removes enclosing quotes from a string.
 * @param {string} str - The string to unquote.
 * @returns {string} - The unquoted string.
 */
function helperUnquote(str) {
  str = str.trim();

  if (str.startsWith('"') && str.endsWith('"')) {
    str = str.slice(1, -1);
  }

  return str;
}

const EOL = ['\r\n', '\n', '\r'];
const EOL_REGEX = new RegExp(EOL.join('|'), 'g');

/**
 * A CSV parser that converts CSV strings to JSON.
 * @class
 */
class CSVParser {
  /**
   * Creates a new CSVParser instance.
   *
   * @param {string} csvText - The CSV string to parse.
   * @param {Object} options - The configuration options for this CSVParser instance.
   * @param {string} options.delimiter - The CSV delimiter character (default: comma).
   * @param {boolean} options.autoTyping - Whether to automatically convert values to their appropriate types.
   * @param {Array<string>} options.arrayFields - The names of fields to be parsed as arrays.
   * @param {Object} options.replaceHeader - The header mapping for this CSVParser instance.
   * @param {string} options.replaceHeader.fieldName
   * @param {Object} options.transform - The transform functions per header for this CSVParser instance.
   * @param {Function} options.transform.fieldName
   * @param {Object} options.validate - The validate functions per header for this CSVParser instance.
   * @param {Function} options.validate.fieldName
   */
  constructor(csvText = '', options = {}) {
    // The default configuration options
    const defaults = {
      delimiter: null,
      autoTyping: false,
      arrayFields: [],
      replaceHeader: {},
      transform: {},
      validate: {},
    };

    const config = { ...defaults, ...options };
    this.options = config;
    /**
     * The array of lines in the CSV string.
     * @type {Array<string>}
     * @private
     */
    this.lines = csvText.trim().split(EOL_REGEX);

    /**
     * The CSV delimiter character.
     * @type {string}
     * @private
     */
    this.delimiter = config.delimiter || this._guessDelimiter(csvText);

    /**
     * The header fields in the CSV string.
     * @type {Array<string>}
     * @private
     */
    this.headers = this.lines.length && this.lines[0].split(this.delimiter).map(helperUnquote);

    /**
     * The index of the current line being parsed.
     * @type {number}
     * @private
     */
    this.currentLineIndex = 1;

    /**
     * The names of fields to be parsed as arrays.
     * @type {Array<string>}
     * @private
     */
    this.arrayFields = config.arrayFields;

    /**
     * Whether to automatically convert values to their appropriate types.
     * @type {boolean}
     * @private
     */
    this.autoTyping = config.autoTyping;

    /** The header mapping for this CSVParser instance.
     * @type {Object}
     * @property {string} header - The name of the header.
     * @property {string} key - The JSON key to use for this header.
     * @private
     */
    this.replaceHeader = config.replaceHeader;

    /**
     * The transform functions per header for this CSVParser instance.
     * @type {Object}
     * @property {string} header - The name of the header.
     * @property {Function} transform - The transform function to use for this header.
     * @private
     */
    this.transform = config.transform;

    /**
     * The validate functions per header for this CSVParser instance.
     * @type {Object}
     * @property {string} header - The name of the header.
     * @property {Function} transform - The validate function to use for this header.
     * @private
     */
    this.validate = config.validate;

    /**
     * The calculate functions for this CSVParser instance.
     * @type {Object}
     * @property {Function} calculate - The calculated function to use object and attach new field in certain way.
     * @private
     */
    this.calculatedColumn = config.calculatedColumn || [];
  }

  /**
   * Parses the next line in the CSV string.
   * Handling quoted values, empty lines and array fields.
   *
   * @returns {Object} - An object containing the values in the line.
   * @private
   */
  _csvLineToJSON(line) {
    const values = [];
    let start = 0;
    let inQuote = false;

    // Loop through each character in the line
    for (let i = 0; i < line.length; i++) {
      if (line[i] === this.delimiter && !inQuote) {
        // If we encounter a delimiter and we're not in a quote, push the value to the array
        values.push(line.slice(start, i).trim());
        // Set the start index to the next character
        start = i + 1;
      } else if (line[i] === '"') {
        // If we encounter a quote character, toggle the inQuote flag
        inQuote = !inQuote;
      }
    }

    // Push the final value to the array
    values.push(line.slice(start).trim());

    // Loop through each value, remove any leading/trailing quotes and split array fields
    for (let i = 0; i < values.length; i++) {
      values[i] = helperUnquote(values[i]);

      if (this.arrayFields.includes(this.headers[i])) {
        // If the header is in the arrayFields array, split the value into an array
        values[i] = values[i]
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value.length > 0);
      }
    }

    return values;
  }

  /** Applies auto typing to the given value.
   *
   * @param {string} value - The value to apply auto typing to.
   * @returns {string|number|boolean|null|undefined} - The auto typed value.
   * @private
   */
  _applyAutoTyping(value) {
    if (value === 'true' || value === 'TRUE') {
      return true;
    } else if (value === 'false' || value === 'FALSE') {
      return false;
    } else if (value === 'null') {
      return null;
    } else if (value === 'undefined') {
      return undefined;
    } else if (value === 'NaN') {
      return NaN;
    } else if (parseFloat(value).toString() === value) {
      return parseFloat(value);
    }

    return value;
  }

  /**
   * Determines the delimiter of a CSV text automatically.
   *
   * @param {string} csvText - The CSV text to analyze.
   * @returns {string|null} - The delimiter character, or null if none could be determined.
   * @private
   */
  _guessDelimiter(string) {
    // Common delimiters in order of likelihood
    const delimiters = [',', ';', '\t', '|'];

    // Count the number of times each delimiter appears in the string
    const results = delimiters.map((delimiter) => {
      const lines = string.split(EOL_REGEX);
      const counts = lines.map((line) => line.split(delimiter).length);
      const sum = counts.reduce((a, b) => a + b, 0);

      // Return the delimiter and the sum
      return { delimiter, sum };
    });

    // Sort the results by the sum
    const sorted = results.sort((a, b) => b.sum - a.sum);

    // Return the delimiter with the highest sum
    return sorted[0].delimiter;
  }

  /**
   * Gets the header fields in the CSV string.
   *
   * @returns {Array<string>} - An array of header field names.
   */
  getHeaders() {
    return this.headers;
  }

  /**
   * Gets the number of non-empty lines in the CSV string.
   *
   * @returns {number} - The number of non-empty lines in the CSV string.
   */
  getNumberOfLines() {
    return this.lines.filter((line) => line.trim().length > 0).length;
  }

  /**
   * Parse the next line of the CSV text
   *
   * Reads the next line of the CSV file and returns an object with key-value pairs
   * representing the CSV columns. If the line is empty or contains only whitespace,
   * the function skips it and reads the next line.
   *
   * @returns {Object} An object with key-value pairs representing the CSV columns, or
   *                   null if there are no more lines to read.
   */
  // eslint-disable-next-line max-statements
  parseNextLine() {
    // Check if we've reached the end of the file
    if (this.currentLineIndex >= this.lines.length) {
      return null;
    }

    // Get the current line and remove any leading/trailing whitespace
    const line = this.lines[this.currentLineIndex].trim();

    if (!line) {
      // If the line is empty or contains only whitespace, skip it and read the next line
      this.currentLineIndex++;
      return this.parseNextLine();
    }

    // Parse the line into an array of values
    const values = this._csvLineToJSON(line);

    if (values.length !== this.headers.length) {
      // If the number of columns doesn't match the number of headers, return an error object
      this.currentLineIndex++;

      return {
        data: null,
        error: {
          lineNumber: this.currentLineIndex,
          message: 'COLUMN_MISMATCH',
          reason: `Line has ${values.length} columns, header has ${this.headers.length} columns.`,
        },
      };
    }

    // Otherwise, create an object with key-value pairs for each column
    let obj = {};
    let error = null;

    for (let j = 0; j < this.headers.length; j++) {
      // Get the key for this column
      const key = this.replaceHeader[this.headers[j]] || this.headers[j];

      obj.lineNumber = this.currentLineIndex;

      if (this.autoTyping) {
        // If auto typing is enabled, apply it to the value
        obj[key] = this._applyAutoTyping(values[j]);
      } else {
        // Otherwise, just use the value as-is
        obj[key] = values[j];
      }

      if (typeof this.transform[key] === 'function') {
        // If transform function is provided, apply it to the value
        // and replace the value with the transformed value
        // If an error occurs within the transform call, set the error object
        try {
          obj[key] = this.transform[key](obj[key]);
        } catch (transformError) {
          error = {
            lineNumber: this.currentLineIndex + 1,
            message: 'TRANSFORM_ERROR',
            reason: String(transformError),
          };
        }
      }

      if (typeof this.validate[key] === 'function') {
        // If validate function is provided, apply it to the value
        // If validation fails, set the error object
        try {
          if (!this.validate[key](obj[key], obj)) {
            throw new Error(`Validation failed for '${key}' with value '${obj[key]}'`);
          }
        } catch (validateError) {
          error = {
            lineNumber: this.currentLineIndex + 1,
            message: 'VALIDATE_ERROR',
            reason: String(validateError),
          };
        }
      }

      if (this.calculatedColumn.length) {
        this.calculatedColumn.forEach((calculateFn) => {
          calculateFn(obj);
        });
      }
    }

    // Move on to the next line and return the data
    this.currentLineIndex++;

    return {
      data: obj,
      error: error,
    };
  }

  /**
   * Parses the entire CSV string.
   *
   * Returns an object containing two arrays: one for the parsed data and one for any encountered errors.
   * The parsed data array will contain an object for each row of the CSV, with each field mapped to its corresponding
   * header name. Any fields defined as array fields will be split by commas into an array.
   * The errors array will contain an object for each encountered error, with the lineNumber, message, and reason of the error.
   *
   * @returns {Object} - An object containing the parsed data and any errors encountered.
   */
  parse() {
    // Initialize empty arrays for the parsed data and errors
    const errors = [];
    const result = [];

    // Loop through each line of the CSV
    let lineObj;

    // Parse each line in the CSV string until there are no more lines to parse
    while ((lineObj = this.parseNextLine()) !== null) {
      if (lineObj.error) {
        errors.push(lineObj.error);
      } else {
        result.push(lineObj.data);
      }
    }

    // Return an object containing the parsed data and any errors
    return {
      data: result,
      errors: errors,
    };
  }
}

export default CSVParser;
